import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const PACKAGES = [
  { nameKey: 'pkg_p1_name', multiplier: 2, label: '×2 (100%)', investment: 21100 },
  { nameKey: 'pkg_p2_name', multiplier: 3, label: '×3 (200%)', investment: 26000 },
  { nameKey: 'pkg_p3_name', multiplier: 4, label: '×4 (300%)', investment: 31000 },
];

export default function Calculator() {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(26000);
  const [selectedPkg, setSelectedPkg] = useState(1);

  const result = useMemo(() => {
    const pkg = PACKAGES[selectedPkg];
    const profit = Math.round(amount * (pkg.multiplier - 1));
    const total = amount + profit;
    const pct = (pkg.multiplier - 1) * 100;
    const investedPct = Math.round((amount / total) * 100);
    return { profit, total, pct, investedPct, profitPct: 100 - Math.round((amount / total) * 100), multiplier: pkg.multiplier };
  }, [amount, selectedPkg]);

  return (
    <section id="calculator" className="py-16 md:py-28 bg-[#0d1117]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-calculator-line text-green-400 text-sm"></i>
            <span className="text-green-400 text-xs font-semibold uppercase tracking-wide">{t('calc_badge')}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-black text-white mb-4">
            {t('calc_title_1')} <span className="text-yellow-400">{t('calc_title_2')}</span>
          </h2>
          <p className="text-white/50 text-sm md:text-base">{t('calc_subtitle')}</p>
        </div>
        <div className="max-w-4xl mx-auto bg-white rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="p-6 md:p-10">
              <h3 className="text-gray-900 font-bold text-lg md:text-xl mb-6">{t('calc_amount_label')}</h3>
              <div className="mb-6">
                <label className="block text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">{t('calc_amount_label')}</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-lg">€</span>
                  <input type="number" min={1000} max={500000} value={amount}
                    onChange={(e) => setAmount(Math.max(1000, Number(e.target.value)))}
                    className="w-full pl-10 pr-4 py-3 md:py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-bold text-base focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all text-sm" />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">{t('calc_package_label')}</label>
                <div className="space-y-2">
                  {PACKAGES.map((pkg, i) => (
                    <button key={pkg.nameKey} onClick={() => { setSelectedPkg(i); setAmount(pkg.investment); }}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all duration-150 cursor-pointer ${selectedPkg === i ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedPkg === i ? 'border-green-500' : 'border-gray-300'}`}>
                          {selectedPkg === i && <div className="w-2 h-2 bg-green-500 rounded-full"></div>}
                        </div>
                        <span className={`text-xs md:text-sm font-semibold text-left ${selectedPkg === i ? 'text-green-700' : 'text-gray-700'}`}>{t(pkg.nameKey)}</span>
                      </div>
                      <span className={`text-sm font-black whitespace-nowrap ml-2 ${selectedPkg === i ? 'text-green-500' : 'text-gray-400'}`}>{pkg.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-time-line text-green-500"></i>
                  <span className="text-green-700 text-xs font-semibold uppercase tracking-wider">{t('calc_duration_label')}</span>
                </div>
                <div className="text-green-900 font-black text-xl md:text-2xl">{t('calc_duration_val')}</div>
                <p className="text-green-600/70 text-xs mt-1">{t('calc_duration_note')}</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-600 to-green-800 p-6 md:p-10 flex flex-col justify-between">
              <div>
                <h3 className="text-white/70 font-semibold text-sm uppercase tracking-wider mb-4">{t('calc_result_title')}</h3>
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-white/20 rounded-2xl px-4 py-2 text-center">
                    <div className="text-white font-black text-4xl leading-none">×{result.multiplier}</div>
                    <div className="text-white/60 text-xs mt-0.5">{t('calc_multiplier')}</div>
                  </div>
                  <div>
                    <div className="text-white/60 text-xs uppercase tracking-wider mb-0.5">{t('calc_net_profit')}</div>
                    <div className="text-white font-black text-3xl md:text-4xl">€{result.profit.toLocaleString()}</div>
                    <div className="flex items-center gap-1 mt-1">
                      <i className="ri-arrow-up-line text-green-300 text-xs"></i>
                      <span className="text-green-300 text-xs font-semibold">{result.pct}% {t('calc_yield')}</span>
                    </div>
                  </div>
                </div>
                <div className="w-full h-px bg-white/20 mb-4"></div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">{t('calc_total')}</span>
                    <span className="text-white font-bold text-lg md:text-xl">€{result.total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">{t('calc_invested_label')}</span>
                    <span className="text-white font-semibold">€{amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">{t('calc_package_row')}</span>
                    <span className="text-white font-semibold text-right text-sm">{t(PACKAGES[selectedPkg].nameKey)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">{t('calc_period')}</span>
                    <span className="text-white font-semibold">{t('calc_period_val')}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-white/50 mb-1.5">
                    <span>{t('calc_bar_invested')}</span>
                    <span>{t('calc_bar_profit')}</span>
                  </div>
                  <div className="flex rounded-full overflow-hidden h-3 bg-white/20">
                    <div className="bg-white transition-all duration-700 rounded-l-full" style={{ width: `${result.investedPct}%` }}></div>
                    <div className="bg-yellow-300 transition-all duration-700 rounded-r-full" style={{ width: `${result.profitPct}%` }}></div>
                  </div>
                </div>
              </div>
              <button className="mt-6 md:mt-8 w-full bg-white hover:bg-gray-50 text-green-700 font-bold py-3.5 md:py-4 rounded-xl transition-all duration-200 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap text-sm">
                {t('calc_choose_btn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
